import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card, CardContent, Typography, Stack, TextField, Button, Box, IconButton,
  Chip, Alert, Skeleton, FormControlLabel, Checkbox, Tooltip, MenuItem,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import PersonOffRoundedIcon from "@mui/icons-material/PersonOffRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../context/TenantContext";

interface Vinculo { personas: string[]; sales_rep_id: string; sales_team_ids?: string[]; enabled: boolean }
interface Equipe { id: string; vendedores: number }
interface UserDetail {
  id: string; name?: string; email?: string; username?: string;
  phone_number?: string; tenants: string[];
  role: string; enabled: boolean; email_verified: boolean;
  required_actions: string[]; stale: boolean; error?: string;
  vinculo?: Vinculo | null;
}
interface Persona { id: string; name: string; description?: string; scope?: string }
interface Acesso { persona_catalog?: Persona[]; areas?: Record<string, boolean> }

// AS QUATRO DO MODELO, decisao do Lucas em 04/11/2026.
//
// O catalogo tem nove personas e a tela oferecia todas. Medido no mesmo dia:
// `owner` e identica a `executive`; `collector` e `customer` nunca foram
// usadas em nenhum dos oito clientes; `supplier` tem lista de capacidades
// VAZIA — quem a recebesse logava e nao fazia nada; `internal_admin` e time
// UpOwl, e o grupo que a abria foi removido em 03/11/2026.
//
// As outras continuam no catalogo e nos vinculos existentes: elas saem da
// OFERTA, nao do sistema.
const ATRIBUIVEIS = ["seller", "supervisor", "buyer", "executive"];

export function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const { tenant } = useTenant();
  const api = useApi();
  const cliente = useQueryClient();

  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [personas, setPersonas] = useState<string[]>([]);
  const [rca, setRca] = useState("");
  const [equipes, setEquipes] = useState<string[]>([]);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  const q = useQuery({
    queryKey: ["user-detail", tenant, id],
    queryFn: () => api.call<UserDetail>(`/api/users/${encodeURIComponent(id ?? "")}`),
    enabled: !!tenant && !!id, staleTime: 60_000,
  });
  const acesso = useQuery({
    queryKey: ["access-personas", tenant],
    queryFn: () => api.call<Acesso>("/api/access"),
    enabled: !!tenant, staleTime: 300_000,
  });
  const equipesQ = useQuery({
    queryKey: ["equipes", tenant],
    queryFn: () => api.call<{ equipes: Equipe[] }>("/api/users/equipes"),
    enabled: !!tenant, staleTime: 300_000,
  });
  const u = q.data;
  const podeGerenciar = acesso.data?.areas?.["usuarios.gerenciar"] === true;

  // O formulário nasce do que está gravado. Sem isto o "Salvar" mandaria vazio
  // e apagaria o nome de quem só queria trocar o telefone.
  useEffect(() => {
    if (!u) return;
    setNome(u.name || "");
    setTelefone(u.phone_number || "");
    setPersonas(u.vinculo?.personas ?? []);
    setRca(u.vinculo?.sales_rep_id ?? "");
    setEquipes(u.vinculo?.sales_team_ids ?? []);
  }, [u?.id, u?.vinculo?.personas?.join(","), u?.vinculo?.sales_rep_id,
      u?.vinculo?.sales_team_ids?.join(",")]);

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ["user-detail", tenant, id] });
    cliente.invalidateQueries({ queryKey: ["users", tenant] });
  };
  const falhou = (e: any) => setErro(e?.detalhe || "Não consegui concluir agora.");
  const deuCerto = (msg: string) => { setErro(""); setAviso(msg); recarregar(); };

  const salvarDados = useMutation({
    mutationFn: () => api.call(`/api/users/${encodeURIComponent(id!)}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nome, telefone }),
    }),
    onSuccess: (r: any) => deuCerto(
      r?.movido_de
        // Trocar o número move o vínculo: dizer só "salvo" esconderia que o
        // número antigo deixou de atender.
        ? `Telefone alterado de ${r.movido_de} para ${r.telefone}. O antigo foi desligado.`
        : "Dados salvos."),
    onError: falhou,
  });

  const salvarPermissoes = useMutation({
    mutationFn: () => api.call(`/api/users/${encodeURIComponent(id!)}/permissoes`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ personas, sales_rep_id: rca, sales_team_ids: equipes }),
    }),
    onSuccess: () => deuCerto("Permissões atualizadas."),
    onError: falhou,
  });

  // TRES `useMutation` SEPARADOS, E NAO UMA FABRICA.
  //
  // `const acao = (c, m) => useMutation(...)` chamava hook dentro de funcao:
  // funciona por acidente enquanto a ordem das chamadas nao muda, e quebra no
  // dia em que uma delas virar condicional. Regra de hooks do React.
  const postar = (caminho: string) => () =>
    api.call(`/api/users/${encodeURIComponent(id!)}/${caminho}`, { method: "POST" });

  const desativar = useMutation({
    mutationFn: postar("desativar"),
    onSuccess: () => deuCerto("Acesso desligado. O usuário e o histórico foram mantidos."),
    onError: falhou,
  });
  const reativar = useMutation({
    mutationFn: postar("reativar"),
    onSuccess: () => deuCerto("Acesso religado."),
    onError: falhou,
  });
  const resetar = useMutation({
    mutationFn: postar("resetar-senha"),
    onSuccess: () => deuCerto("Código de redefinição enviado por e-mail."),
    onError: falhou,
  });

  const ehVendedor = personas.includes("seller");
  const semRca = ehVendedor && !rca.trim();
  const ehSupervisor = personas.includes("supervisor");
  const semEquipe = ehSupervisor && !equipes.length;
  // As do modelo MAIS as que a pessoa ja tem. Sem a segunda metade, abrir o
  // usuario com uma persona legada e salvar qualquer coisa a apagaria sem
  // ninguem ver — ha um `supervisor` real na jotuje nessa situacao.
  const ofertadas = (acesso.data?.persona_catalog ?? []).filter(
    (p) => ATRIBUIVEIS.includes(p.id) || personas.includes(p.id));
  const ocupado = salvarDados.isPending || salvarPermissoes.isPending
    || desativar.isPending || reativar.isPending || resetar.isPending;
  const ativo = u?.vinculo ? u.vinculo.enabled : u?.enabled !== false;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton component={RouterLink} to="/usuarios" size="small">
          <ArrowBackRoundedIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">Usuários</Typography>
      </Stack>

      <PageHeader
        title={u?.name || (q.isLoading ? "Carregando..." : "Usuário")}
        subtitle={<span style={{ fontFamily: "monospace", fontSize: 12 }}>{id}</span>}
      />

      {q.error && <Alert severity="error">Erro ao carregar usuário.</Alert>}
      {u?.stale && u.error === "forbidden" && (
        <Alert severity="error">Você não tem permissão para ver esse usuário.</Alert>
      )}
      {u?.stale && u.error && u.error !== "forbidden" && (
        <Alert severity="warning">Detalhes indisponíveis ({u.error}).</Alert>
      )}
      {erro && <Alert severity="error" onClose={() => setErro("")}>{erro}</Alert>}
      {aviso && <Alert severity="success" onClose={() => setAviso("")}>{aviso}</Alert>}
      {!ativo && <Alert severity="warning">Acesso desligado. A Sophia não responde para este número.</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.dark" }}>Dados básicos</Typography>
          {q.isLoading ? (
            <Stack spacing={2}><Skeleton height={40} /><Skeleton height={40} /><Skeleton height={40} /></Stack>
          ) : (
            <Stack spacing={2}>
              <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)}
                fullWidth disabled={!podeGerenciar || ocupado} />
              <TextField label="Telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)}
                fullWidth disabled={!podeGerenciar || ocupado}
                helperText="55 + DDD + número. Nos DDD 85 e 88 o nono dígito é removido automaticamente."
                InputProps={{ sx: { fontFamily: "monospace" } }} />
              <TextField label="Email" value={u?.email || ""} fullWidth InputProps={{ readOnly: true }}
                helperText={u?.email ? (u?.email_verified ? "verificado" : "não verificado") : "sem e-mail — entra pelo WhatsApp"} />
              <TextField label="Username" value={u?.username || ""} fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }} />
              {podeGerenciar && (
                <Box>
                  <Button variant="contained" disabled={ocupado} onClick={() => salvarDados.mutate()}>
                    Salvar dados
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.dark" }}>Permissões em {tenant}</Typography>
          {q.isLoading ? <Skeleton width="60%" /> : (
            <Stack spacing={2}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {(u?.tenants ?? []).map((t) => <Chip key={t} label={t} size="small" />)}
              </Stack>
              {!u?.vinculo && (
                <Alert severity="info">
                  Sem permissão definida neste cliente. Escolha abaixo e salve.
                </Alert>
              )}
              <Box>
                {ofertadas.map((p) => (
                  <Tooltip key={p.id} title={p.description || ""} placement="right">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={personas.includes(p.id)}
                          disabled={!podeGerenciar || ocupado}
                          onChange={(e) => setPersonas((atual) =>
                            e.target.checked
                              ? [...atual, p.id]
                              : atual.filter((x) => x !== p.id))}
                        />
                      }
                      label={ATRIBUIVEIS.includes(p.id)
                        ? p.name
                        : `${p.name} (fora do modelo atual)`}
                    />
                  </Tooltip>
                ))}
              </Box>
              {ehVendedor && (
                <TextField
                  label="RCA (código do vendedor no ERP)" value={rca}
                  onChange={(e) => setRca(e.target.value)} required
                  error={semRca} disabled={!podeGerenciar || ocupado}
                  helperText={semRca
                    // `rca_only` sem recorte não vê número nenhum: o vendedor
                    // ficaria com acesso e sem dado, que é pior que sem acesso.
                    ? "Vendedor sem RCA não vê nenhum dado."
                    : "Define de quem são os números que esta pessoa vê."}
                  InputProps={{ sx: { fontFamily: "monospace" } }}
                />
              )}
              {ehSupervisor && (
                <TextField
                  select SelectProps={{ multiple: true, native: false }}
                  label="Equipes" value={equipes}
                  onChange={(e) => setEquipes(
                    typeof e.target.value === "string"
                      ? e.target.value.split(",")
                      : (e.target.value as unknown as string[]))}
                  required error={semEquipe} disabled={!podeGerenciar || ocupado}
                  helperText={semEquipe
                    // `team_only` sem equipe filtra por lista vazia: o
                    // supervisor veria zero, e pareceria que a equipe nao vendeu.
                    ? "Supervisor sem equipe não vê nenhum dado."
                    : "Ele vê os vendedores destas equipes, e só deles."}
                >
                  {(equipesQ.data?.equipes ?? []).map((eq) => (
                    <MenuItem key={eq.id} value={eq.id}>
                      Equipe {eq.id} · {eq.vendedores} vendedor{eq.vendedores === 1 ? "" : "es"}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {podeGerenciar && (
                <Box>
                  <Button variant="contained"
                    disabled={ocupado || semRca || semEquipe || !personas.length}
                    onClick={() => salvarPermissoes.mutate()}>
                    Salvar permissões
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      {podeGerenciar && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.dark" }}>Ações</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Tooltip title={u?.email ? "" : "Só para quem tem e-mail: o código vai por e-mail."}>
                <span>
                  <Button variant="outlined" startIcon={<RestartAltRoundedIcon />}
                    disabled={ocupado || !u?.email} onClick={() => resetar.mutate()}>
                    Resetar senha
                  </Button>
                </span>
              </Tooltip>
              {ativo ? (
                <Button variant="outlined" color="error" startIcon={<PersonOffRoundedIcon />}
                  disabled={ocupado} onClick={() => desativar.mutate()}>
                  Desativar acesso
                </Button>
              ) : (
                <Button variant="outlined" color="success" startIcon={<HowToRegRoundedIcon />}
                  disabled={ocupado} onClick={() => reativar.mutate()}>
                  Reativar acesso
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              Desativar desliga o acesso e mantém o usuário e o histórico. Nada aqui apaga ninguém.
            </Typography>
          </CardContent>
        </Card>
      )}
    </>
  );
}
